/**
 * DataPrepStep
 *
 * Step 2b of the CSV/Excel import wizard. Runs automatic heuristics on the
 * parsed rows and surfaces clarifying questions for data-quality issues that
 * cannot be resolved by column mapping alone.
 *
 * Detects:
 *  - Zero-sentinel rows: rows where every cell is "0" or blank
 *  - Null sentinel "0": >20% of all cell values are exactly "0"
 *  - Boolean columns: columns where ≥80% of non-empty values are "x"/"0"
 *  - Section column: first column with low cardinality used as a grouping label
 *
 * Emits a DataPrepConfig consumed by applyDataPrepConfig() before column
 * mapping begins.
 */

import { useMemo } from 'react';
import { Filter, ToggleLeft, Layers, CheckCircle2 } from 'lucide-react';
import type { DataPrepConfig } from '../../../lib/excelService';

// Re-export so callers can import both from this module if they prefer
export type { DataPrepConfig };

interface DetectedState {
  zeroRowCount: number;
  zeroRatio: number;
  booleanCols: string[];
  sectionColumn: string | null;
  sectionValues: string[];
  hasIssues: boolean;
}

export interface DataPrepStepProps {
  headers: string[];
  rows: Record<string, unknown>[];
  config: DataPrepConfig;
  onChange: (config: DataPrepConfig) => void;
}

// ── Detection helpers ─────────────────────────────────────────────────────────

function normalize(v: unknown): string {
  return String(v ?? '').trim();
}

function isBlankOrZero(v: unknown): boolean {
  const s = normalize(v);
  return s === '' || s === '0';
}

function detectIssues(headers: string[], rows: Record<string, unknown>[]): DetectedState {
  if (rows.length === 0) {
    return { zeroRowCount: 0, zeroRatio: 0, booleanCols: [], sectionColumn: null, sectionValues: [], hasIssues: false };
  }

  // Zero/blank sentinel rows
  const zeroRowCount = rows.filter(row =>
    Object.values(row).every(v => isBlankOrZero(v))
  ).length;

  // Overall null-sentinel ratio
  const allValues = rows.flatMap(r => Object.values(r)).filter(v => normalize(v) !== '');
  const zeroCount = allValues.filter(v => normalize(v) === '0').length;
  const zeroRatio = allValues.length > 0 ? zeroCount / allValues.length : 0;

  // Boolean columns (x/X pattern)
  const booleanCols: string[] = [];
  for (const header of headers) {
    const colVals = rows.map(r => normalize(r[header])).filter(v => v !== '');
    if (colVals.length < 3) continue;
    const boolLike = colVals.filter(v => v === 'x' || v === 'X' || v === '0' || v === '1').length;
    const hasX = colVals.some(v => v === 'x' || v === 'X');
    if (hasX && boolLike / colVals.length >= 0.8) {
      booleanCols.push(header);
    }
  }

  // Section / grouping column — first column with low cardinality
  let sectionColumn: string | null = null;
  let sectionValues: string[] = [];
  const firstHeader = headers[0];
  if (firstHeader) {
    const nonBlank = rows
      .map(r => normalize(r[firstHeader]))
      .filter(v => v !== '' && v !== '0');
    const uniqueVals = Array.from(new Set(nonBlank));
    const avgPerVal = nonBlank.length / Math.max(uniqueVals.length, 1);
    if (uniqueVals.length >= 2 && uniqueVals.length <= 15 && avgPerVal >= 2) {
      sectionColumn = firstHeader;
      sectionValues = uniqueVals.slice(0, 6);
    }
  }

  const hasIssues =
    zeroRowCount > 0 ||
    zeroRatio > 0.2 ||
    booleanCols.length > 0 ||
    sectionColumn !== null;

  return { zeroRowCount, zeroRatio, booleanCols, sectionColumn, sectionValues, hasIssues };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DataPrepStep({ headers, rows, config, onChange }: DataPrepStepProps) {
  const detected = useMemo(() => detectIssues(headers, rows), [headers, rows]);

  const update = (patch: Partial<DataPrepConfig>) => onChange({ ...config, ...patch });

  if (!detected.hasIssues) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle2 className="h-12 w-12 text-[var(--green)] mb-3" />
        <p className="text-lg font-medium text-[var(--text-pri)]">Looks clean</p>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          No data-quality issues detected. Click Continue to map columns.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--text-sec)]">
        We found a few things worth reviewing before column mapping. Choose how to handle each one.
      </p>

      {/* Zero / blank sentinel rows */}
      {detected.zeroRowCount > 0 && (
        <IssueCard
          icon={<Filter className="h-4 w-4" />}
          title={`${detected.zeroRowCount} empty row${detected.zeroRowCount !== 1 ? 's' : ''} detected`}
          description={`${detected.zeroRowCount} row${detected.zeroRowCount !== 1 ? 's' : ''} where every cell is "0" or blank — likely padding between sections.`}
        >
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={config.skipZeroRows}
              onChange={e => update({ skipZeroRows: e.target.checked })}
              className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
            />
            <span className="text-[var(--text-sec)]">
              Skip these rows during import
              <span className="ml-1 text-[var(--text-ter)]">(recommended)</span>
            </span>
          </label>
        </IssueCard>
      )}

      {/* Null sentinel "0" */}
      {detected.zeroRatio > 0.2 && (
        <IssueCard
          icon={<ToggleLeft className="h-4 w-4" />}
          title={`${Math.round(detected.zeroRatio * 100)}% of cells contain "0"`}
          description='Many cells use "0" as a placeholder for missing data rather than a real value.'
        >
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={config.treatZeroAsNull}
              onChange={e => update({ treatZeroAsNull: e.target.checked })}
              className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
            />
            <span className="text-[var(--text-sec)]">
              Treat "0" as empty/missing
              <span className="ml-1 text-[var(--text-ter)]">(recommended)</span>
            </span>
          </label>
        </IssueCard>
      )}

      {/* Boolean columns */}
      {detected.booleanCols.length > 0 && (
        <IssueCard
          icon={<ToggleLeft className="h-4 w-4" />}
          title={`${detected.booleanCols.length} Yes/No column${detected.booleanCols.length !== 1 ? 's' : ''} detected`}
          description={`These columns contain only "x" (yes) and "0" (no): ${detected.booleanCols.join(', ')}.`}
        >
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={config.booleanColumns.length === detected.booleanCols.length}
                onChange={e =>
                  update({ booleanColumns: e.target.checked ? detected.booleanCols : [] })
                }
                className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
              />
              <span className="text-[var(--text-sec)]">Import as Yes / No values</span>
            </label>
            {config.booleanColumns.length > 0 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer ml-5">
                <input
                  type="checkbox"
                  checked={config.autoCreateBooleanFields}
                  onChange={e => update({ autoCreateBooleanFields: e.target.checked })}
                  className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                />
                <span className="text-[var(--text-sec)]">
                  Auto-create workspace custom fields for these columns
                </span>
              </label>
            )}
          </div>
        </IssueCard>
      )}

      {/* Section / grouping column */}
      {detected.sectionColumn && (
        <IssueCard
          icon={<Layers className="h-4 w-4" />}
          title={`"${detected.sectionColumn}" looks like a grouping column`}
          description={`Values: ${detected.sectionValues.join(', ')}${detected.sectionValues.length === 6 ? '…' : ''}. How should this column be imported?`}
        >
          <div className="space-y-1.5">
            {(
              [
                { value: 'custom_field', label: 'Create a custom field', hint: 'Stored per-item as a workspace field' },
                { value: 'tag', label: 'Add as a tag', hint: "Appended to each item's tag list" },
                { value: 'skip', label: 'Skip this column', hint: 'Ignore it during import' },
              ] as const
            ).map(opt => (
              <label key={opt.value} className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="section-col-treatment"
                  value={opt.value}
                  checked={config.sectionColumnTreatment === opt.value}
                  onChange={() => update({ sectionColumnTreatment: opt.value })}
                  className="mt-0.5 border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="text-[var(--text-pri)] font-medium">{opt.label}</span>
                  <span className="ml-1.5 text-[var(--text-ter)]">{opt.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </IssueCard>
      )}
    </div>
  );
}

// ── IssueCard sub-component ───────────────────────────────────────────────────

function IssueCard({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-[var(--rule)] p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 p-1.5 bg-[var(--mustard-dim)] text-[var(--mustard)] flex-shrink-0">
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-[var(--text-pri)]">{title}</p>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">{description}</p>
        </div>
      </div>
      <div className="pl-9">{children}</div>
    </div>
  );
}

export default DataPrepStep;
